import React, { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '@/utils/api-request';
import {
    ShieldCheck, ChevronLeft, ChevronRight, CheckCircle2, XCircle, EyeOff,
    MessageSquare, Bot, User, AlertTriangle, Globe, Loader2,
} from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';
import SettingsSlideModal from '@/components/Modal/SettingsSlideModal';

/**
 * Parent Community moderation (super admin).
 *
 * Approval gates *indexability*, never visibility — a parent's question is live
 * the moment they post it. What a moderator decides here is whether YellowSlate
 * is willing to put a page about a named real school into Google.
 */

interface Thread {
    id: number;
    title: string;
    excerpt: string;
    slug: string | null;
    origin: string;
    contentType: string | null;
    replyCount: number;
    /** Distinct chat sessions that asked this. For chat threads, the N>=3 anonymity threshold. */
    contributingSessions: number;
    reportCount: number;
    isApproved: boolean;
    isIndexable: boolean;
    topic: { slug: string; name: string } | null;
    location: string | null;
    school: string | null;
    createdAt: string;
    moderatedAt: string | null;
}

interface Counts {
    pending: number;
    approved: number;
    indexable: number;
    not_indexable: number;
    incomplete: number;
    from_chat: number;
}

interface LogEntry {
    log_id: number;
    target_type: string;
    target_id: number;
    action: string;
    actor_name: string | null;
    reason: string | null;
    created_at: string;
}

type StatusKey = 'not_indexable' | 'pending' | 'approved' | 'reported' | 'incomplete';

// `not_indexable` leads because it is the real backlog. The migrated threads all
// inherited is_approved = 1 from the old school-page surface, so "pending" reads
// as empty even though thousands of threads have never been looked at.
const TABS: { key: StatusKey; label: string; countKey?: keyof Counts; hint: string }[] = [
    { key: 'not_indexable', label: 'Needs review', countKey: 'not_indexable', hint: 'Live, complete, but not yet cleared for search' },
    { key: 'pending', label: 'Unapproved', countKey: 'pending', hint: 'Never approved by anyone' },
    { key: 'approved', label: 'Approved', countKey: 'approved', hint: 'Cleared by a moderator' },
    { key: 'reported', label: 'Reported', hint: 'Flagged by a parent' },
    { key: 'incomplete', label: 'Incomplete', countKey: 'incomplete', hint: 'Missing city, locality, topic or slug — a data problem, not a decision' },
];

const PAGE_SIZES = [25, 50, 100];

const CommunityModerationAdmin: React.FC = () => {
    const [threads, setThreads] = useState<Thread[]>([]);
    const [counts, setCounts] = useState<Counts | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [status, setStatus] = useState<StatusKey>('not_indexable');
    const [origin, setOrigin] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [hasMore, setHasMore] = useState(false);

    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [busyId, setBusyId] = useState<number | null>(null);
    const [isBulkRunning, setIsBulkRunning] = useState(false);

    const [detail, setDetail] = useState<Thread | null>(null);
    const [log, setLog] = useState<LogEntry[]>([]);
    const [isLogLoading, setIsLogLoading] = useState(false);

    const [rejectTarget, setRejectTarget] = useState<Thread | null>(null);
    const [rejectReason, setRejectReason] = useState('');

    const fetchQueue = useCallback(async (nextPage: number) => {
        try {
            setIsLoading(true);
            const qs = new URLSearchParams({
                status,
                page: String(nextPage),
                limit: String(pageSize),
            });
            if (origin) qs.set('origin', origin);

            const response = await apiRequest(`/api/community-moderation/queue?${qs.toString()}`);
            if (response.success && response.data) {
                setThreads(response.data.threads || []);
                setCounts(response.data.counts || null);
                setHasMore(!!response.data.hasMore);
                setPage(nextPage);
                setSelected(new Set());
            } else {
                showToast(response.message || 'Failed to load the moderation queue', 'error');
            }
        } catch (error) {
            console.error('Error loading moderation queue:', error);
            showToast('Failed to load the moderation queue', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [status, origin, pageSize]);

    useEffect(() => {
        fetchQueue(1);
    }, [fetchQueue]);

    const openDetail = async (thread: Thread) => {
        setDetail(thread);
        setLog([]);
        try {
            setIsLogLoading(true);
            const response = await apiRequest(
                `/api/community-moderation/log?targetType=thread&targetId=${thread.id}`
            );
            if (response.success && response.data) setLog(response.data.entries || []);
        } catch (error) {
            console.error('Error loading moderation log:', error);
        } finally {
            setIsLogLoading(false);
        }
    };

    const act = async (thread: Thread, action: 'approve' | 'unpublish', reason?: string) => {
        try {
            setBusyId(thread.id);
            const response = await apiRequest(`/api/community-moderation/thread/${thread.id}/${action}`, {
                method: 'POST',
                body: reason ? { reason } : {},
            });
            if (response.success) {
                // approve returns a note explaining when approval did not make the
                // thread indexable — surface it verbatim rather than assuming success.
                showToast(response.data?.note || `Thread ${action}ed`, 'success');
                fetchQueue(page);
            } else {
                showToast(response.message || `Failed to ${action} the thread`, 'error');
            }
        } catch (error) {
            console.error(`Error on ${action}:`, error);
            showToast(`Failed to ${action} the thread`, 'error');
        } finally {
            setBusyId(null);
        }
    };

    const confirmReject = async () => {
        if (!rejectTarget) return;
        try {
            setBusyId(rejectTarget.id);
            const response = await apiRequest(`/api/community-moderation/thread/${rejectTarget.id}/reject`, {
                method: 'POST',
                body: { reason: rejectReason || undefined },
            });
            if (response.success) {
                showToast('Thread rejected and hidden', 'success');
                setRejectTarget(null);
                setRejectReason('');
                fetchQueue(page);
            } else {
                showToast(response.message || 'Failed to reject the thread', 'error');
            }
        } catch (error) {
            console.error('Error rejecting thread:', error);
            showToast('Failed to reject the thread', 'error');
        } finally {
            setBusyId(null);
        }
    };

    const bulkApprove = async () => {
        const ids = Array.from(selected);
        if (!ids.length) return;
        try {
            setIsBulkRunning(true);
            const response = await apiRequest('/api/community-moderation/bulk-approve', {
                method: 'POST',
                body: { ids },
            });
            if (response.success && response.data) {
                const { approved, nowIndexable } = response.data;
                showToast(`${approved} approved, ${nowIndexable} now indexable`, 'success');
                fetchQueue(page);
            } else {
                showToast(response.message || 'Bulk approve failed', 'error');
            }
        } catch (error) {
            console.error('Error bulk approving:', error);
            showToast('Bulk approve failed', 'error');
        } finally {
            setIsBulkRunning(false);
        }
    };

    const toggleOne = (id: number) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        setSelected((prev) => (prev.size === threads.length ? new Set() : new Set(threads.map((t) => t.id))));
    };

    const formatDate = (value: string) => {
        if (!value) return '-';
        return new Date(value).toLocaleDateString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
        });
    };

    const activeTab = TABS.find((t) => t.key === status);

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-8 py-4">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3" style={{ flex: '0 0 45%' }}>
                        <div className="p-2 bg-indigo-600 rounded-lg text-white flex-shrink-0">
                            <ShieldCheck className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-3">
                                <h1 className="text-lg font-semibold text-gray-900">Community Moderation</h1>
                                {counts && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-600 text-white">
                                        {Number(counts.indexable || 0).toLocaleString()} indexable
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-gray-500">
                                Approval controls whether a thread can reach Google — not whether parents can see it
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 justify-end" style={{ flex: '0 0 55%' }}>
                        <select
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            {PAGE_SIZES.map((n) => (
                                <option key={n} value={n}>{n} per page</option>
                            ))}
                        </select>

                        <select
                            value={origin}
                            onChange={(e) => setOrigin(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">All sources</option>
                            <option value="composer">Written by a parent</option>
                            <option value="chat">Derived from chat</option>
                        </select>

                        {selected.size > 0 && (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={bulkApprove}
                                disabled={isBulkRunning}
                                leftIcon={<CheckCircle2 size={16} />}
                            >
                                {isBulkRunning ? 'Approving...' : `Approve ${selected.size} selected`}
                            </Button>
                        )}
                    </div>
                </div>

                {/* Status tabs */}
                <div className="flex items-center gap-1 mt-4 -mb-4">
                    {TABS.map((tab) => {
                        const count = tab.countKey && counts ? Number(counts[tab.countKey] || 0) : null;
                        const isActive = tab.key === status;
                        return (
                            <button
                                key={tab.key}
                                title={tab.hint}
                                onClick={() => setStatus(tab.key)}
                                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                                    isActive
                                        ? 'border-indigo-600 text-indigo-700'
                                        : 'border-transparent text-gray-500 hover:text-gray-800'
                                }`}
                            >
                                {tab.label}
                                {count !== null && (
                                    <span className="ml-2 text-xs text-gray-400">{count.toLocaleString()}</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {activeTab && (
                <p className="px-8 pt-5 pb-1 text-xs text-gray-500">{activeTab.hint}</p>
            )}

            {/* Table */}
            <div className="flex-1 overflow-auto px-4 mt-2">
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full table-auto border-collapse">
                        <thead className="bg-brandColor text-white sticky top-0 z-10">
                            <tr>
                                <th className="px-4 py-3 w-10">
                                    <input
                                        type="checkbox"
                                        checked={threads.length > 0 && selected.size === threads.length}
                                        onChange={toggleAll}
                                        className="rounded border-gray-300"
                                    />
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Thread</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Source</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Topic</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Where</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Replies</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">State</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Created</th>
                                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider whitespace-nowrap">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                                        <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                                        Loading threads...
                                    </td>
                                </tr>
                            ) : threads.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                                        Nothing in this queue.
                                    </td>
                                </tr>
                            ) : (
                                threads.map((thread) => (
                                    <tr key={thread.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <input
                                                type="checkbox"
                                                checked={selected.has(thread.id)}
                                                onChange={() => toggleOne(thread.id)}
                                                className="rounded border-gray-300"
                                            />
                                        </td>
                                        <td className="px-4 py-3 max-w-md">
                                            <button
                                                onClick={() => openDetail(thread)}
                                                className="text-left text-sm font-medium text-gray-900 hover:text-indigo-700"
                                            >
                                                {thread.title || '(untitled)'}
                                            </button>
                                            <p className="text-xs text-gray-500 truncate">{thread.excerpt}</p>
                                            {thread.school && (
                                                <p className="text-xs text-gray-400 mt-0.5">{thread.school}</p>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {thread.origin === 'chat' ? (
                                                <span
                                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700"
                                                    title="Distinct parent chats that asked this. Chat questions publish only at 3 or more."
                                                >
                                                    <Bot size={12} /> Chat · {thread.contributingSessions} parents
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">
                                                    <User size={12} /> Parent
                                                    {thread.contributingSessions > 0 && (
                                                        <span title="Chats that asked the same thing">· +{thread.contributingSessions} from chats</span>
                                                    )}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                                            {thread.topic?.name || <span className="text-red-500">none</span>}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                                            {thread.location || <span className="text-red-500">none</span>}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1">
                                                <MessageSquare size={13} className="text-gray-400" />
                                                {thread.replyCount}
                                            </span>
                                            {thread.reportCount > 0 && (
                                                <span className="ml-2 inline-flex items-center gap-1 text-red-600">
                                                    <AlertTriangle size={13} />
                                                    {thread.reportCount}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {thread.isIndexable ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                                                    <Globe size={12} /> Indexable
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                                                    noindex
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                                            {formatDate(thread.createdAt)}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-right">
                                            <div className="inline-flex items-center gap-1">
                                                <Button
                                                    variant="outline"
                                                    size="xs"
                                                    disabled={busyId === thread.id}
                                                    onClick={() => act(thread, 'approve')}
                                                    leftIcon={<CheckCircle2 size={14} />}
                                                >
                                                    Approve
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="xs"
                                                    disabled={busyId === thread.id}
                                                    onClick={() => act(thread, 'unpublish')}
                                                    leftIcon={<EyeOff size={14} />}
                                                >
                                                    De-index
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    size="xs"
                                                    disabled={busyId === thread.id}
                                                    onClick={() => { setRejectTarget(thread); setRejectReason(''); }}
                                                    leftIcon={<XCircle size={14} />}
                                                >
                                                    Reject
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination — the endpoint returns hasMore, not a total, so there is
                no last-page control to offer. */}
            <div className="bg-white border-t border-gray-200 px-8 py-3 flex items-center justify-between">
                <span className="text-sm text-gray-500">Page {page}</span>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1 || isLoading}
                        onClick={() => fetchQueue(page - 1)}
                        leftIcon={<ChevronLeft size={16} />}
                    >
                        Previous
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={!hasMore || isLoading}
                        onClick={() => fetchQueue(page + 1)}
                    >
                        Next <ChevronRight size={16} className="inline ml-1" />
                    </Button>
                </div>
            </div>

            {/* Thread detail + audit trail */}
            <SettingsSlideModal
                isOpen={!!detail}
                onClose={() => setDetail(null)}
                title={detail?.title || 'Thread'}
                subtitle={detail ? [detail.school, detail.location].filter(Boolean).join(' · ') : ''}
                icon={<ShieldCheck className="h-5 w-5" />}
            >
                {detail && (
                    <div className="space-y-6">
                        <div>
                            <h3 className="text-xs font-semibold uppercase text-gray-500 mb-2">Content</h3>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{detail.excerpt}</p>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-500">Source</span>
                                <p className="text-gray-900">{detail.origin === 'chat' ? 'Derived from chat' : 'Written by a parent'}</p>
                            </div>
                            <div>
                                <span className="text-gray-500">Topic</span>
                                <p className="text-gray-900">{detail.topic?.name || 'none'}</p>
                            </div>
                            <div>
                                <span className="text-gray-500">Approved</span>
                                <p className="text-gray-900">{detail.isApproved ? 'Yes' : 'No'}</p>
                            </div>
                            <div>
                                <span className="text-gray-500">Indexable</span>
                                <p className="text-gray-900">{detail.isIndexable ? 'Yes' : 'No'}</p>
                            </div>
                            <div className="col-span-2">
                                <span className="text-gray-500">Slug</span>
                                <p className="text-gray-900 break-all">{detail.slug || <span className="text-red-500">missing</span>}</p>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-xs font-semibold uppercase text-gray-500 mb-2">Moderation history</h3>
                            {isLogLoading ? (
                                <p className="text-sm text-gray-500">Loading...</p>
                            ) : log.length === 0 ? (
                                <p className="text-sm text-gray-500">Never moderated.</p>
                            ) : (
                                <ul className="space-y-2">
                                    {log.map((entry) => (
                                        <li key={entry.log_id} className="text-sm border-l-2 border-gray-200 pl-3">
                                            <span className="font-medium text-gray-900">{entry.action}</span>
                                            <span className="text-gray-500"> by {entry.actor_name || 'unknown'} · {formatDate(entry.created_at)}</span>
                                            {entry.reason && <p className="text-gray-600 text-xs mt-0.5">{entry.reason}</p>}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}
            </SettingsSlideModal>

            {/* Reject needs a reason on the record — rejection hides a parent's own
                words, which is the one action here they may come back and ask about. */}
            <SettingsSlideModal
                isOpen={!!rejectTarget}
                onClose={() => setRejectTarget(null)}
                title="Reject thread"
                subtitle={rejectTarget?.title}
                icon={<XCircle className="h-5 w-5" />}
                width="480px"
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                        This hides the thread from parents and removes it from search. It is recorded
                        in the moderation log.
                    </p>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                        <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            rows={3}
                            placeholder="Spam, abusive, personal information, off-topic..."
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => setRejectTarget(null)}>Cancel</Button>
                        <Button
                            variant="danger"
                            size="sm"
                            disabled={busyId === rejectTarget?.id}
                            onClick={confirmReject}
                        >
                            Reject thread
                        </Button>
                    </div>
                </div>
            </SettingsSlideModal>
        </div>
    );
};

export default CommunityModerationAdmin;
